// @flow
import invariant from 'invariant';
import { truncateToUTF8Bytes } from 'utils/string';
import { isEmpty, filterObject, values } from 'utils/functional';
import { initialFacetFilters } from 'utils/search/searchFacets';
import type { SearchResults } from 'api/search';
import type { Action } from '../actions/postSearch';

export type PostSearchResults = SearchResults;

const searchFacets: { [string]: SearchFacet } = {
  articleCategoriesFacet: {
    id: 'articleCategoriesFacet',
    name: 'categories',
    title: getText('Category'),
    type: 'array',
    lazy: true,
    optionsArray: [],
    options: [], // Lazy loaded
    optionTitles: {} // Lazy loaded
  },
  articleAuthorFacet: {
    id: 'articleAuthorFacet',
    name: 'author.slug',
    title: getText('Author'),
    type: 'string',
    lazy: false,
    options: ['DUMMY_OPTION'],
    optionTitles: {}
  },
  articleTagsFacet: {
    id: 'articleTagsFacet',
    name: 'tags.slug',
    title: getText('Tags'),
    type: 'array',
    lazy: false,
    options: ['DUMMY_OPTION'],
    optionTitles: {}
  }
};

const initialFacetsThatArentLoaded: Array<string> = Object.keys(
  filterObject(searchFacets, (_, f) => !!f.lazy)
);

export type State = {
  query: string,
  pageIndex: number,
  lastLoadedPageIndex: number,
  searchId: number, // Incrementing id
  resultsByPage: { [string]: PostSearchResults },
  resultsForSearchId?: ?string,
  initialized: boolean,
  initialSearchStarted: boolean,
  initialResultsLoaded: boolean,
  isSearching: boolean,
  queryID: ?string,
  searchFacets: { [string]: SearchFacet },
  facetsThatArentLoaded: Array<string>,
  filters: SearchFilters
};

const initialSearchState = {
  query: '',
  pageIndex: 0,
  lastLoadedPageIndex: 0,
  searchId: 0, // Incrementing id
  resultsByPage: {},
  initialized: false,
  initialSearchStarted: false,
  initialResultsLoaded: false,
  isSearching: false,
  queryID: null,
  searchFacets,
  filters: {},
  facetsThatArentLoaded: initialFacetsThatArentLoaded
};

const defaultState: State = initialSearchState;

export default function postSearch(
  state: State = defaultState,
  action: Action
) {
  switch (action.type) {
    case 'POST_SEARCH_SET_QUERY': {
      return {
        ...state,
        searchId: state.searchId + 1,
        pageIndex: 0, // New search, reset page
        query: action.query
      };
    }

    case 'POST_SEARCH_RESET_PAGE': {
      return {
        ...state,
        searchId: state.searchId + 1,
        pageIndex: 0 // New search, reset page
      };
    }

    case 'POST_SEARCH_RESET_FILTERS': {
      const filters = {
        ...initialFacetFilters(
          values(state.searchFacets),
          state.facetsThatArentLoaded,
          {}
        ),
        articleCategoriesFacet: state.filters.articleCategoriesFacet
      };

      return {
        ...state,
        searchId: state.searchId + 1,
        pageIndex: 0, // New search, reset page
        landingPagePath: null,
        filters
      };
    }

    case 'POST_SEARCH_SET_PAGE_INDEX': {
      return {
        ...state,
        pageIndex: action.pageIndex
      };
    }

    case 'POST_SEARCH_INITIALIZE': {
      const { page, pq } = action.queryParams;
      const filters: SearchFilters = initialFacetFilters(
        values(searchFacets),
        initialFacetsThatArentLoaded,
        action.queryParams
      );

      return {
        ...initialSearchState,
        initialized: true,
        query: pq ? truncateToUTF8Bytes(pq || '', 512) : state.query,
        pageIndex: page ? Number(page) - 1 : state.pageIndex,
        filters
      };
    }

    case 'POST_SEARCH_REINITIALIZE': {
      const filters: SearchFilters = initialFacetFilters(
        values(state.searchFacets),
        state.facetsThatArentLoaded,
        { pq: '' }
      );

      return {
        ...state,
        initialized: true,
        query: '',
        pageIndex: 0,
        filters
      };
    }

    case 'POST_SEARCH_UNINITIALIZE': {
      return {
        ...state,
        facetsThatArentLoaded: initialFacetsThatArentLoaded,
        initialized: false
      };
    }

    case 'POST_SEARCH_STARTED': {
      return {
        ...state,
        initialSearchStarted: true,
        isSearching: true,
        queryID: null
      };
    }

    case 'POST_SEARCH_RESULTS_SUCCESS': {
      // If the results are from an older search, ignore them
      const lastResults =
        state.resultsByPage[state.lastLoadedPageIndex.toString()];
      if (lastResults && lastResults.timestamp > action.results.timestamp) {
        return state;
      }

      const newSearch = action.results.searchId !== state.resultsForSearchId;

      return {
        ...state,
        queryID: action.results.queryID,
        resultsByPage: newSearch
          ? {
              [action.results.page]: action.results
            }
          : {
              ...state.resultsByPage,
              [action.results.page]: action.results
            },
        lastLoadedPageIndex: action.results.page,
        resultsForSearchId: action.results.searchId,
        initialResultsLoaded: true,
        isSearching: false
      };
    }

    case 'POST_SEARCH_CLEAR_RESULTS': {
      return {
        ...state,
        resultsByPage: {},
        resultsForSearchId: null
      };
    }

    case 'POST_SEARCH_SET_FACET_OPTIONS': {
      const { options, optionTitles, optionsArray, facetKey, reload } = action;
      const facet = state.searchFacets[facetKey];
      const facetsThatArentLoaded: Array<string> = state.facetsThatArentLoaded.filter(
        key => key !== facetKey
      );
      if (!facet && isEmpty(options)) {
        return state;
      }
      // const sideFacets = sideFacetsForType(searchFacets, state.filters.type);
      // const hasFacet = sideFacets.find(f => f.id === facet.id);
      const filterValue = facet.id && state.filters[facet.id];
      const updatedFilters = reload
        ? {
            ...state.filters,
            articleCategoriesFacet: []
          }
        : facet.id // && hasFacet
        ? {
            ...state.filters,
            [facet.id]: filterValue || options
          }
        : state.filters;

      const newSearchFacets: { [string]: SearchFacet } = {
        // Consider all search facets, not just ones in the current state
        ...searchFacets,
        ...state.searchFacets,
        [facetKey]: {
          ...facet,
          options,
          optionTitles,
          optionsArray
        }
      };

      return {
        ...state,
        // This needs to get bumped if we change filter values
        // But if we dont, then it doesnt need to get changed
        searchId:
          filterValue && filterValue !== options
            ? state.searchId + 1
            : state.searchId,
        facetsThatArentLoaded,
        // Only set the filter value if the current filter.type supports this
        // facet
        filters: updatedFilters,
        searchFacets: newSearchFacets
      };
    }

    case 'POST_SEARCH_SET_FILTER': {
      const facet = action.key;
      const value = action.value;

      const filters = Object.assign(
        {},
        // Setting the type filter resets the other filters.
        state.filters,
        { [facet]: value }
      );
      return {
        ...state,
        searchId: state.searchId + 1,
        pageIndex: 0, // New search, reset page
        filters
      };
    }

    case 'POST_SEARCH_SELECT_FILTER_OPTION': {
      const facet = action.key;
      const filter = state.filters[facet];
      const selectedOptions = [
        ...((Array.isArray(filter) && filter) || []),
        action.option
      ];

      return {
        ...state,
        searchId: state.searchId + 1,
        pageIndex: 0, // New search, reset page
        filters: {
          ...state.filters,
          [facet]: selectedOptions
        }
      };
    }

    case 'POST_SEARCH_DESELECT_FILTER_OPTION': {
      const facet = action.key;
      const option = action.option;
      invariant(Array.isArray(state.filters[facet]), 'Missing search filter');
      const selectedOptions = state.filters[facet].filter(v => v !== option);

      return {
        ...state,
        searchId: state.searchId + 1,
        pageIndex: 0, // New search, reset page
        filters: {
          ...state.filters,
          [facet]: selectedOptions
        }
      };
    }

    case 'POST_SEARCH_SELECT_ALL_FILTER_OPTIONS': {
      const facet = action.key;
      return {
        ...state,
        searchId: state.searchId + 1,
        pageIndex: 0, // New search, reset page
        filters: {
          ...state.filters,
          [facet]: state.searchFacets[facet].options
        }
      };
    }

    default:
      // this causes a flow error if there are any unhandled actions
      (action: empty); // eslint-disable-line no-unused-expressions
      return state;
  }
}

// Utility operations on state
export const results = (state: State) =>
  state.resultsByPage[state.pageIndex.toString()];
